import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRightLeft, Building2, CheckCircle2, CircleSlash, ClipboardList, FileEdit,
  Filter, History, Loader2, Pencil, Plus, Send, UserRound, Users, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/constants";

const todayIso = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-700 border-slate-200",
  Ready: "bg-sky-50 text-sky-700 border-sky-200",
  Sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

const EMPTY_FILTERS = { company: "all", recruiter_id: "all", job_id: "all", date_from: "", date_to: "" };

export default function Lineups() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("Draft");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [jobs, setJobs] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState(null);

  // create/edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ job_id: "", lineup_date: todayIso(), assigned_recruiter_id: "", remarks: "" });
  const [eligible, setEligible] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [pickStage, setPickStage] = useState("all");
  const [selectedApps, setSelectedApps] = useState([]);

  // cancel dialog
  const [cancelOf, setCancelOf] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  // reschedule dialog
  const [reschedOf, setReschedOf] = useState(null);
  const [reschedForm, setReschedForm] = useState({ application_id: "", new_job_id: "", reason: "" });

  // detail/history dialogs
  const [detailOf, setDetailOf] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // sent remarks dialog (admin)
  const [remarksOf, setRemarksOf] = useState(null);
  const [remarksText, setRemarksText] = useState("");

  useEffect(() => {
    api.get("/jobs", { params: { status: "all", limit: 100 } }).then((res) => setJobs(res.data || [])).catch(() => {});
    if (isAdmin) {
      api.get("/users/recruiters").then((res) => setRecruiters(res.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const load = useCallback(() => {
    setError(false);
    const params = {};
    if (tab !== "All") params.status = tab;
    if (filters.company !== "all") params.company = filters.company;
    if (isAdmin && filters.recruiter_id !== "all") params.recruiter_id = filters.recruiter_id;
    if (filters.job_id !== "all") params.job_id = filters.job_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const calls = [api.get("/lineups", { params }), api.get("/lineups/kpis")];
    if (isAdmin) calls.push(api.get("/lineups/reports"));
    Promise.all(calls)
      .then(([l, k, r]) => {
        setItems(l.data);
        setKpis(k.data);
        if (r) setReports(r.data);
        const names = [...new Set(l.data.map((x) => x.company_display).filter(Boolean))];
        setCompanyOptions((prev) => [...new Set([...prev, ...names])]);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tab, filters, isAdmin]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const loadEligible = useCallback((jobId, stage) => {
    if (!jobId) { setEligible([]); return; }
    setEligibleLoading(true);
    const params = {};
    if (stage && stage !== "all") params.stage = stage;
    api.get("/lineups/eligible-applications", { params: { job_id: jobId, ...params } })
      .then((res) => setEligible(res.data))
      .catch(() => setEligible([]))
      .finally(() => setEligibleLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ job_id: "", lineup_date: todayIso(), assigned_recruiter_id: "", remarks: "" });
    setSelectedApps([]);
    setEligible([]);
    setPickStage("all");
    setFormOpen(true);
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({ job_id: l.job_id, lineup_date: l.lineup_date, assigned_recruiter_id: l.assigned_recruiter_id || "", remarks: l.remarks || "" });
    setSelectedApps(l.candidates.filter((c) => c.entry_status === "Active").map((c) => c.application_id));
    setPickStage("all");
    loadEligible(l.job_id, "all");
    setFormOpen(true);
  };

  const onJobChange = (jobId) => {
    setForm({ ...form, job_id: jobId });
    setSelectedApps([]);
    loadEligible(jobId, pickStage);
  };

  const toggleApp = (id) => {
    setSelectedApps((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submitForm = async () => {
    if (!form.job_id) return toast.error("Choose a job");
    if (selectedApps.length === 0) return toast.error("Select at least one candidate");
    if (!form.lineup_date) return toast.error("Lineup date is required");
    setBusy(true);
    try {
      const payload = {
        job_id: form.job_id,
        application_ids: selectedApps,
        lineup_date: form.lineup_date,
        remarks: form.remarks || null,
      };
      if (isAdmin && form.assigned_recruiter_id) payload.assigned_recruiter_id = form.assigned_recruiter_id;
      if (editing) {
        const res = await api.put(`/lineups/${editing.id}`, payload);
        toast.success(`Lineup ${res.data.lineup_code} updated`);
      } else {
        const res = await api.post("/lineups", payload);
        toast.success(`Lineup ${res.data.lineup_code} created (Draft)`);
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (l, action, reason) => {
    setBusy(true);
    try {
      await api.put(`/lineups/${l.id}/status`, { action, reason: reason || null });
      toast.success(`Lineup ${l.lineup_code} ${action === "cancel" ? "cancelled" : `marked ${action === "ready" ? "Ready" : "Sent"}`}`);
      setCancelOf(null); setCancelReason("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReschedule = async () => {
    if (!reschedForm.application_id) return toast.error("Choose the candidate to reschedule");
    if (!reschedForm.new_job_id) return toast.error("Choose the new job");
    if (reschedForm.reason.trim().length < 3) return toast.error("A reschedule reason is mandatory");
    setBusy(true);
    try {
      const res = await api.post(`/lineups/${reschedOf.id}/reschedule`, {
        application_id: reschedForm.application_id,
        new_job_id: reschedForm.new_job_id,
        reason: reschedForm.reason.trim(),
      });
      toast.success(`Candidate rescheduled — new lineup ${res.data.new_lineup.lineup_code} created`);
      setReschedOf(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitRemarks = async () => {
    if (!remarksText.trim()) return toast.error("Remarks cannot be empty");
    setBusy(true);
    try {
      await api.put(`/lineups/${remarksOf.id}/remarks`, { remarks: remarksText.trim() });
      toast.success("Remarks updated");
      setRemarksOf(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = (l) => {
    setHistoryOf(l);
    setHistoryLoading(true);
    api.get(`/lineups/${l.id}/history`)
      .then((res) => setHistory(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setHistoryLoading(false));
  };

  const canEdit = (l) => l.status === "Draft" && (isAdmin || l.created_by === user.id || l.assigned_recruiter_id === user.id);
  const canReady = canEdit;
  const canCancel = (l) => (l.status === "Draft" || l.status === "Ready") &&
    (isAdmin || (l.status === "Draft" && (l.created_by === user.id || l.assigned_recruiter_id === user.id)));
  const canReschedule = (l) => (l.status === "Draft" || l.status === "Ready") &&
    (isAdmin || l.candidates.some((c) => c.recruiter_id === user.id && c.entry_status === "Active"));

  const filtersActive = filters.company !== "all" || filters.recruiter_id !== "all" ||
    filters.job_id !== "all" || filters.date_from || filters.date_to;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Lineup Management</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Prepare and track candidate lineups per company and job — no more manual sheets.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-lg shrink-0" data-testid="lineup-create-button">
          <Plus className="h-4 w-4 mr-2" /> Create Lineup
        </Button>
      </div>

      {/* Dashboard cards */}
      {kpis && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiCard label="Draft" value={kpis.draft} icon={FileEdit} tone="slate" testId="lineup-kpi-draft" />
          <KpiCard label="Ready" value={kpis.ready} icon={CheckCircle2} tone="primary" testId="lineup-kpi-ready" />
          <KpiCard label="Sent" value={kpis.sent} icon={Send} tone="success" testId="lineup-kpi-sent" />
          <KpiCard label="Cancelled" value={kpis.cancelled} icon={CircleSlash} tone="warning" testId="lineup-kpi-cancelled" />
          <KpiCard label="Rescheduled" value={kpis.rescheduled} icon={ArrowRightLeft} tone="slate" testId="lineup-kpi-rescheduled" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-lg flex-wrap h-auto">
          <TabsTrigger value="Draft" className="rounded-md" data-testid="lineup-tab-draft">Draft</TabsTrigger>
          <TabsTrigger value="Ready" className="rounded-md" data-testid="lineup-tab-ready">Ready</TabsTrigger>
          <TabsTrigger value="Sent" className="rounded-md" data-testid="lineup-tab-sent">Sent</TabsTrigger>
          <TabsTrigger value="Cancelled" className="rounded-md" data-testid="lineup-tab-cancelled">Cancelled</TabsTrigger>
          {isAdmin && <TabsTrigger value="All" className="rounded-md" data-testid="lineup-tab-all">All</TabsTrigger>}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="rounded-xl border-border shadow-none" data-testid="lineup-filters">
        <CardContent className="p-4">
          <div className="flex flex-col xl:flex-row xl:items-end gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground shrink-0 xl:pb-2.5">
              <Filter className="h-4 w-4" /> Filters
            </div>
            <div className={`grid grid-cols-2 ${isAdmin ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 flex-1`}>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Company</Label>
                <Select value={filters.company} onValueChange={(v) => setFilters({ ...filters, company: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="lineup-filter-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All companies</SelectItem>
                    {companyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Recruiter</Label>
                  <Select value={filters.recruiter_id} onValueChange={(v) => setFilters({ ...filters, recruiter_id: v })}>
                    <SelectTrigger className="bg-white h-9" data-testid="lineup-filter-recruiter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All recruiters</SelectItem>
                      {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Job</Label>
                <Select value={filters.job_id} onValueChange={(v) => setFilters({ ...filters, job_id: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="lineup-filter-job"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All jobs</SelectItem>
                    {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} · {j.hiring_brand}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From (lineup date)</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="lineup-filter-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="lineup-filter-to" />
              </div>
            </div>
            {filtersActive && (
              <Button variant="ghost" size="sm" className="rounded-lg shrink-0" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="lineup-filter-reset">
                <X className="h-3.5 w-3.5 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="lineups-error">
          <p className="text-sm text-muted-foreground">Could not load lineups.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No lineups here"
          description="Create a lineup by choosing a job and selecting candidates from its applications." />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lineup ID</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Candidates</TableHead>
                  <TableHead>Assigned Recruiter</TableHead>
                  <TableHead>Lineup Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} className="hover:bg-muted/60" data-testid="lineup-row">
                    <TableCell className="font-mono text-xs">
                      <button className="hover:text-primary hover:underline" onClick={() => setDetailOf(l)} data-testid={`lineup-detail-${l.id}`}>
                        {l.lineup_code}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{l.company_display || "—"}</span>
                    </TableCell>
                    <TableCell className="text-sm">{l.job_title}</TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><Users className="h-3 w-3 text-muted-foreground" />{l.candidate_count}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><UserRound className="h-3 w-3 text-muted-foreground" />{l.recruiter?.name || "—"}</span>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(l.lineup_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full ${STATUS_STYLES[l.status]}`} data-testid="lineup-status-badge">{l.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {canEdit(l) && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white" onClick={() => openEdit(l)} data-testid={`lineup-edit-${l.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {l.status === "Draft" && canReady(l) && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white" disabled={busy}
                            onClick={() => changeStatus(l, "ready")} data-testid={`lineup-ready-${l.id}`}>
                            Mark Ready
                          </Button>
                        )}
                        {l.status === "Ready" && isAdmin && (
                          <Button size="sm" className="h-8 rounded-lg" disabled={busy}
                            onClick={() => changeStatus(l, "sent")} data-testid={`lineup-sent-${l.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1.5" /> Mark Sent
                          </Button>
                        )}
                        {l.status === "Sent" && isAdmin && (
                          <Button size="sm" variant="ghost" className="h-8 rounded-lg"
                            onClick={() => { setRemarksOf(l); setRemarksText(l.remarks || ""); }} data-testid={`lineup-remarks-${l.id}`}>
                            Remarks
                          </Button>
                        )}
                        {canReschedule(l) && (
                          <Button size="sm" variant="ghost" className="h-8 rounded-lg"
                            onClick={() => { setReschedOf(l); setReschedForm({ application_id: "", new_job_id: "", reason: "" }); }}
                            data-testid={`lineup-reschedule-${l.id}`}>
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canCancel(l) && (
                          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-destructive hover:text-destructive"
                            onClick={() => { setCancelOf(l); setCancelReason(""); }} data-testid={`lineup-cancel-${l.id}`}>
                            <CircleSlash className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openHistory(l)} data-testid={`lineup-history-${l.id}`}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Admin reports */}
      {isAdmin && reports && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="lineup-reports">
          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Company-wise lineups</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
              {reports.by_company.length === 0 && <p className="text-sm text-muted-foreground">No lineups yet.</p>}
              {reports.by_company.map((r) => (
                <div key={r.company} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid="report-company-row">
                  <span className="text-sm">{r.company}</span>
                  <Badge variant="outline" className="rounded-full">{r.count}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Recruiter-wise lineups</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
              {reports.by_recruiter.length === 0 && <p className="text-sm text-muted-foreground">No lineups yet.</p>}
              {reports.by_recruiter.map((r) => (
                <div key={r.recruiter} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid="report-recruiter-row">
                  <span className="text-sm">{r.recruiter}</span>
                  <Badge variant="outline" className="rounded-full">{r.count}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={(v) => !v && setFormOpen(false)}>
        <DialogContent className="sm:max-w-lg rounded-xl max-h-[90vh] overflow-y-auto" data-testid="lineup-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Edit draft — ${editing.lineup_code}` : "Create lineup"}</DialogTitle>
            <DialogDescription>
              One lineup covers one company & job. Candidates come only from that job's applications.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Job</Label>
              <Select value={form.job_id} onValueChange={onJobChange}>
                <SelectTrigger className="bg-white" data-testid="lineup-job-select"><SelectValue placeholder="Choose job" /></SelectTrigger>
                <SelectContent>
                  {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} · {j.hiring_brand}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.job_id && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label>Candidates <span className="text-muted-foreground">({selectedApps.length} selected)</span></Label>
                  <Select value={pickStage} onValueChange={(v) => { setPickStage(v); loadEligible(form.job_id, v); }}>
                    <SelectTrigger className="bg-white h-8 w-40 text-xs" data-testid="lineup-stage-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All stages</SelectItem>
                      {["Applied", "Screening", "Interested", "Interview Scheduled", "Going", "Selected"].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-lg border border-border max-h-52 overflow-y-auto divide-y divide-border" data-testid="lineup-candidate-picker">
                  {eligibleLoading && <div className="p-3"><Skeleton className="h-10 w-full rounded" /></div>}
                  {!eligibleLoading && eligible.length === 0 && (
                    <p className="text-sm text-muted-foreground p-3">No applications found for this job{!isAdmin ? " assigned to you" : ""}.</p>
                  )}
                  {!eligibleLoading && eligible.map((a) => (
                    <label key={a.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/60" data-testid="lineup-candidate-option">
                      <Checkbox checked={selectedApps.includes(a.id)} onCheckedChange={() => toggleApp(a.id)} />
                      <span className="text-sm flex-1 min-w-0">
                        <span className="font-medium">{a.candidate_name}</span>
                        <span className="text-xs text-muted-foreground ml-2 font-mono">{a.app_code}</span>
                      </span>
                      <Badge variant="outline" className="rounded-full text-[10px] shrink-0">{a.stage}</Badge>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Lineup date</Label>
                <Input type="date" className="bg-white" value={form.lineup_date}
                  onChange={(e) => setForm({ ...form, lineup_date: e.target.value })} data-testid="lineup-date-input" />
              </div>
              {isAdmin && (
                <div className="space-y-1.5">
                  <Label>Assigned recruiter <span className="text-muted-foreground">(optional)</span></Label>
                  <Select value={form.assigned_recruiter_id || "auto"} onValueChange={(v) => setForm({ ...form, assigned_recruiter_id: v === "auto" ? "" : v })}>
                    <SelectTrigger className="bg-white" data-testid="lineup-recruiter-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (from allocation)</SelectItem>
                      {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Remarks <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="lineup-remarks-input" />
            </div>
            <Button onClick={submitForm} disabled={busy} className="w-full rounded-lg" data-testid="lineup-form-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save changes" : "Create Draft Lineup"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelOf} onOpenChange={(v) => !v && setCancelOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="lineup-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel lineup — {cancelOf?.lineup_code}</DialogTitle>
            <DialogDescription>Cancellation is terminal and requires a reason. History is preserved forever.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cancellation reason <span className="text-destructive">*</span></Label>
              <Textarea rows={2} className="bg-white" value={cancelReason} placeholder="Why is this lineup being cancelled? (mandatory)"
                onChange={(e) => setCancelReason(e.target.value)} data-testid="lineup-cancel-reason-input" />
            </div>
            <Button variant="destructive" disabled={busy} className="w-full rounded-lg"
              onClick={() => {
                if (cancelReason.trim().length < 3) return toast.error("A cancellation reason is mandatory");
                changeStatus(cancelOf, "cancel", cancelReason.trim());
              }} data-testid="lineup-cancel-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Cancel Lineup
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={!!reschedOf} onOpenChange={(v) => !v && setReschedOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="lineup-reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reschedule candidate — {reschedOf?.lineup_code}</DialogTitle>
            <DialogDescription>
              The original lineup stays unchanged; the candidate entry is closed as Rescheduled and a new Draft lineup is created for the new company/job.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Candidate</Label>
              <Select value={reschedForm.application_id} onValueChange={(v) => setReschedForm({ ...reschedForm, application_id: v })}>
                <SelectTrigger className="bg-white" data-testid="reschedule-candidate-select"><SelectValue placeholder="Choose candidate" /></SelectTrigger>
                <SelectContent>
                  {(reschedOf?.candidates || []).filter((c) => c.entry_status === "Active" && (isAdmin || c.recruiter_id === user.id)).map((c) => (
                    <SelectItem key={c.application_id} value={c.application_id}>{c.candidate_name} · {c.app_code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>New job (company)</Label>
              <Select value={reschedForm.new_job_id} onValueChange={(v) => setReschedForm({ ...reschedForm, new_job_id: v })}>
                <SelectTrigger className="bg-white" data-testid="reschedule-job-select"><SelectValue placeholder="Choose new job" /></SelectTrigger>
                <SelectContent>
                  {jobs.filter((j) => j.id !== reschedOf?.job_id).map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.title} · {j.hiring_brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea rows={2} className="bg-white" value={reschedForm.reason} placeholder="Why is the candidate being rescheduled? (mandatory, audited)"
                onChange={(e) => setReschedForm({ ...reschedForm, reason: e.target.value })} data-testid="reschedule-reason-input" />
            </div>
            <Button onClick={submitReschedule} disabled={busy} className="w-full rounded-lg" data-testid="reschedule-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reschedule Candidate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sent remarks dialog (admin) */}
      <Dialog open={!!remarksOf} onOpenChange={(v) => !v && setRemarksOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="lineup-remarks-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit remarks — {remarksOf?.lineup_code}</DialogTitle>
            <DialogDescription>Sent lineups are immutable except remarks.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea rows={3} className="bg-white" value={remarksText}
              onChange={(e) => setRemarksText(e.target.value)} data-testid="lineup-remarks-edit-input" />
            <Button onClick={submitRemarks} disabled={busy} className="w-full rounded-lg" data-testid="lineup-remarks-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Remarks
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailOf} onOpenChange={(v) => !v && setDetailOf(null)}>
        <DialogContent className="sm:max-w-lg rounded-xl max-h-[85vh] overflow-y-auto" data-testid="lineup-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detailOf?.lineup_code}</DialogTitle>
            <DialogDescription>
              {detailOf?.company_display} · {detailOf?.job_title} · {formatDate(detailOf?.lineup_date)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(detailOf?.candidates || []).map((c) => (
              <div key={c.application_id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3" data-testid="lineup-detail-candidate">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{c.candidate_name} <span className="font-mono text-xs text-muted-foreground ml-1">{c.candidate_code}</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.app_code} · stage at addition: {c.stage_at_addition} · {c.recruiter_name || "—"}</p>
                </div>
                {c.entry_status === "Rescheduled" ? (
                  <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 shrink-0">
                    Rescheduled → {c.rescheduled_to_code}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">Active</Badge>
                )}
              </div>
            ))}
            {detailOf?.remarks && <p className="text-xs text-muted-foreground whitespace-pre-line">Remarks: {detailOf.remarks}</p>}
            {detailOf?.cancellation_reason && <p className="text-xs text-red-600">Cancelled: {detailOf.cancellation_reason}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="lineup-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">History — {historyOf?.lineup_code}</DialogTitle>
            <DialogDescription>Immutable log of every change. Never edited or deleted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {historyLoading && <Skeleton className="h-24 w-full rounded-lg" />}
            {!historyLoading && history.map((e) => (
              <div key={e.id} className="rounded-lg border border-border p-3" data-testid="lineup-history-item">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    {e.event_type.replaceAll("_", " ")}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                </div>
                {e.previous_status && (
                  <p className="text-sm mt-1.5">{e.previous_status}<span className="text-muted-foreground mx-1.5">→</span><span className="font-medium">{e.new_status}</span></p>
                )}
                {e.cancellation_reason && <p className="text-xs text-red-600 mt-1">Reason: {e.cancellation_reason}</p>}
                {e.details && (
                  <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    {e.details.candidate && <p>Candidate: <span className="text-foreground">{e.details.candidate}</span></p>}
                    {e.details.previous_company && (
                      <p>{e.details.previous_company} / {e.details.previous_job}
                        <span className="mx-1">→</span>
                        <span className="text-foreground">{e.details.new_company} / {e.details.new_job}</span></p>
                    )}
                    {(e.details.previous_recruiter || e.details.new_recruiter) && (
                      <p>Recruiter: {e.details.previous_recruiter || "—"}
                        <span className="mx-1">→</span>
                        <span className="text-foreground">{e.details.new_recruiter || "—"}</span></p>
                    )}
                    {e.details.reason && <p>Reason: {e.details.reason}</p>}
                    {(e.details.from_lineup || e.details.to_lineup) && (
                      <p className="font-mono">{e.details.from_lineup ? `from ${e.details.from_lineup}` : `to ${e.details.to_lineup}`}</p>
                    )}
                    {e.details.company && !e.details.previous_company && <p>{e.details.company} / {e.details.job} · {e.details.candidates} candidate(s)</p>}
                  </div>
                )}
                {e.remarks && !e.details && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{e.remarks}</p>}
                <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span></p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
